/**
 * Operational Dashboard
 * Real-time supervisor dashboard with live updates
 */
import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, TrendingUp, TrendingDown, Clock,
  Truck, Package, CheckCircle, XCircle, AlertCircle, Maximize2,
  Minimize2, RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import dashboardService from '../services/dashboardService';
import { useDashboardWebSocket } from '../hooks/useDashboardWebSocket';

// Import sub-components
import LiveTruckStatus from '../components/dashboard/LiveTruckStatus';
import DockUtilization from '../components/dashboard/DockUtilization';
import QCStatusBoard from '../components/dashboard/QCStatusBoard';
import TodaysMetrics from '../components/dashboard/TodaysMetrics';
import AlertsPanel from '../components/dashboard/AlertsPanel';
import SupplierPerformance from '../components/dashboard/SupplierPerformance';
import TrendChart from '../components/TrendChart';

const OperationalDashboard = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [trendPeriod, setTrendPeriod] = useState('daily');

  // Fetch initial dashboard data
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardService.getDashboardSummary(),
    refetchInterval: 30000, // Refetch every 30 seconds as backup
    onSuccess: (data) => {
      setDashboardData(data);
      setLastRefresh(new Date());
    }
  });

  // Fetch trends data
  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['dashboard-trends', trendPeriod],
    queryFn: () => dashboardService.getPerformanceTrends(trendPeriod, {
      days: trendPeriod === 'daily' ? 7 : undefined,
      weeks: trendPeriod === 'weekly' ? 4 : undefined
    }),
    refetchInterval: 60000, // Refetch every minute
  });

  // Handle WebSocket updates
  const handleDashboardUpdate = useCallback((update) => {
    console.log('Dashboard update received:', update);

    // Merge update with existing data
    setDashboardData((prev) => {
      if (!prev) return update;

      return {
        ...prev,
        ...update,
        // Handle specific section updates
        live_trucks: update.live_trucks || prev.live_trucks,
        dock_utilization: update.dock_utilization || prev.dock_utilization,
        qc_status: update.qc_status || prev.qc_status,
        todays_metrics: update.todays_metrics || prev.todays_metrics,
        alerts: update.alerts || prev.alerts,
        supplier_performance: update.supplier_performance || prev.supplier_performance
      };
    });

    setLastRefresh(new Date());
  }, []);

  // WebSocket connection
  const { isConnected, requestUpdate } = useDashboardWebSocket(handleDashboardUpdate);

  // Toggle full screen
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  // Handle fullscreen change event
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  // Manual refresh
  const handleRefresh = () => {
    refetch();
    requestUpdate();
  };

  const displayData = dashboardData || data;

  if (isLoading && !displayData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-100 ${isFullScreen ? 'p-4' : 'p-6'}`}>
      {/* Header */}
      <div className="mb-6 bg-white rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Activity className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className={`font-bold text-gray-800 ${isFullScreen ? 'text-2xl' : 'text-3xl'}`}>
                Operational Dashboard
              </h1>
              <p className="text-sm text-gray-500">
                Real-time supervisor control center
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Connection Status */}
            <div className="flex items-center space-x-2">
              {isConnected ? (
                <>
                  <Wifi className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-red-600 font-medium">Disconnected</span>
                </>
              )}
            </div>

            {/* Last Update */}
            <div className="text-sm text-gray-600">
              <Clock className="w-4 h-4 inline mr-1" />
              Updated: {lastRefresh.toLocaleTimeString()}
            </div>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Refresh Now"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            {/* Full Screen Toggle */}
            <button
              onClick={toggleFullScreen}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
            >
              {isFullScreen ? (
                <Minimize2 className="w-5 h-5" />
              ) : (
                <Maximize2 className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Today's Metrics - Top Row */}
        <div className="col-span-12">
          <TodaysMetrics data={displayData?.todays_metrics} isFullScreen={isFullScreen} />
        </div>

        {/* Live Truck Status - Left Column (60%) */}
        <div className="col-span-12 lg:col-span-7">
          <LiveTruckStatus data={displayData?.live_trucks} isFullScreen={isFullScreen} />
        </div>

        {/* Alerts Panel - Right Column (40%) */}
        <div className="col-span-12 lg:col-span-5">
          <AlertsPanel data={displayData?.alerts} isFullScreen={isFullScreen} />
        </div>

        {/* Dock Utilization - Full Width */}
        <div className="col-span-12">
          <DockUtilization data={displayData?.dock_utilization} isFullScreen={isFullScreen} />
        </div>

        {/* QC Status Board - Left (50%) */}
        <div className="col-span-12 lg:col-span-6">
          <QCStatusBoard data={displayData?.qc_status} isFullScreen={isFullScreen} />
        </div>

        {/* Supplier Performance - Right (50%) */}
        <div className="col-span-12 lg:col-span-6">
          <SupplierPerformance data={displayData?.supplier_performance} isFullScreen={isFullScreen} />
        </div>

        {/* Performance Trends - Full Width */}
        <div className="col-span-12">
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Performance Trends</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setTrendPeriod('daily')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    trendPeriod === 'daily'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Daily (7 days)
                </button>
                <button
                  onClick={() => setTrendPeriod('weekly')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    trendPeriod === 'weekly'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Weekly (4 weeks)
                </button>
              </div>
            </div>
          </div>

          {trendsLoading ? (
            <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center h-96">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : trendsData ? (
            <TrendChart
              title="SLA Compliance & Performance Metrics"
              data={trendsData.data}
              metrics={[
                { key: 'sla_breaches', label: 'SLA Breaches' },
                { key: 'arrivals', label: 'Truck Arrivals' },
                { key: 'completions', label: 'Completions' },
                { key: 'sla_compliance_rate', label: 'SLA Compliance %' },
                { key: 'avg_processing_time', label: 'Avg Processing Time' },
              ]}
              period={trendPeriod}
              summary={trendsData.summary}
              chartType="line"
            />
          ) : null}
        </div>
      </div>

      {/* Full Screen Mode Indicator */}
      {isFullScreen && (
        <div className="fixed bottom-4 left-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          Full Screen Mode • Press ESC to exit
        </div>
      )}
    </div>
  );
};

export default OperationalDashboard;
